/**
 * Patent Suits Style Graph - Observable HQ pattern implementation
 * Relationship strength-based clustering
 * https://observablehq.com/@d3/mobile-patent-suits
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography } from '@mui/material';
import useTokenExaminerStore from '../../stores/tokenExaminerStore';
import { getFaanaHealthColor } from '../../utils/faanaScoring';
import { getContrastText } from '../../utils/colorContrast';

// eslint-disable-next-line max-lines-per-function
function PatentSuitsGraph() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const {
    _relationships,
    entities,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Render patent suits style graph
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || !filteredRelationships.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Prepare data with relationship strength grouping
    const nodes = Array.from(entities.values()).map(entity => {
      const entityRelationships = filteredRelationships.filter(r =>
        (r.sourceEntity?.crossEntityHash || r.sourceEntityHash) === entity.crossEntityHash ||
        (r.targetEntity?.crossEntityHash || r.targetEntityHash) === entity.crossEntityHash
      );

      const avgStrength = entityRelationships.length > 0
        ? entityRelationships.reduce((sum, r) => sum + (r.overallHealth || 0), 0) / entityRelationships.length
        : 0;

      // Group by relationship strength
      let group = 'weak';
      if (avgStrength >= 70) group = 'strong';
      else if (avgStrength >= 40) group = 'moderate';

      return {
        id: entity.crossEntityHash,
        name: entity.name,
        type: entity.type,
        group,
        avgStrength,
        connectionCount: entityRelationships.length
      };
    });

    const links = filteredRelationships.map(rel => ({
      source: rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash,
      target: rel.targetEntity?.crossEntityHash || rel.targetEntityHash,
      strength: rel.overallHealth || 50,
      type: rel.relationshipType,
      value: (rel.overallHealth || 50) / 10 // Scale for link width
    }));

    // Group colors
    const groupColors = {
      strong: '#4CAF50',
      moderate: '#FF9800',
      weak: '#F44336'
    };

    // Create main container
    const container = svg.append('g');

    // Create force simulation with strength-based clustering
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links)
        .id(d => d.id)
        .distance(d => 120 - (d.strength / 2))
        .strength(d => d.strength / 100)
      )
      .force('charge', d3.forceManyBody()
        .strength(d => -50 - (d.avgStrength * 2))
      )
      .force('center', d3.forceCenter(dimensions.width / 2, dimensions.height / 2))
      .force('collision', d3.forceCollide()
        .radius(d => 20 + (d.connectionCount * 2))
      )
      // Group nodes by strength
      .force('cluster', () => {
        const strength = 0.15;
        const groupCenters = {
          strong: { x: dimensions.width * 0.25, y: dimensions.height * 0.3 },
          moderate: { x: dimensions.width * 0.5, y: dimensions.height * 0.7 },
          weak: { x: dimensions.width * 0.75, y: dimensions.height * 0.3 }
        };

        nodes.forEach(d => {
          const center = groupCenters[d.group];
          if (center) {
            d.vx += (center.x - d.x) * strength;
            d.vy += (center.y - d.y) * strength;
          }
        });
      })
      .alphaDecay(0.02);

    // Add group labels and backgrounds
    const groups = ['strong', 'moderate', 'weak'];
    const groupCenters = {
      strong: { x: dimensions.width * 0.25, y: dimensions.height * 0.3 },
      moderate: { x: dimensions.width * 0.5, y: dimensions.height * 0.7 },
      weak: { x: dimensions.width * 0.75, y: dimensions.height * 0.3 }
    };

    groups.forEach(group => {
      const center = groupCenters[group];

      // Group background
      container.append('circle')
        .attr('cx', center.x)
        .attr('cy', center.y)
        .attr('r', 80)
        .attr('fill', groupColors[group])
        .attr('opacity', 0.1)
        .attr('stroke', groupColors[group])
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');

      // Group label
      container.append('text')
        .attr('x', center.x)
        .attr('y', center.y - 90)
        .attr('text-anchor', 'middle')
        .style('font-weight', 'bold')
        .style('font-size', '14px')
        .style('fill', groupColors[group])
        .text(`${group.charAt(0).toUpperCase() + group.slice(1)} Relationships`);
    });

    // Create links with varying thickness
    const linkElements = container.selectAll('.link')
      .data(links)
      .enter().append('line')
      .attr('class', 'link')
      .attr('stroke', d => getFaanaHealthColor(d.strength))
      .attr('stroke-width', d => Math.max(1, d.value))
      .attr('opacity', 0.6)
      .attr('stroke-linecap', 'round');

    // Create nodes
    const nodeElements = container.selectAll('.node')
      .data(nodes)
      .enter().append('g')
      .attr('class', 'node')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      );

    // Node circles
    nodeElements.append('circle')
      .attr('r', d => 12 + (d.connectionCount * 2))
      .attr('fill', d => groupColors[d.group])
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .on('mouseenter', function(event, d) {
        // Highlight connected links
        linkElements
          .transition()
          .duration(200)
          .attr('opacity', link =>
            (link.source.id === d.id || link.target.id === d.id) ? 1 : 0.1
          )
          .attr('stroke-width', link =>
            (link.source.id === d.id || link.target.id === d.id)
              ? Math.max(2, link.value * 1.5)
              : Math.max(1, link.value)
          );
      })
      .on('mouseleave', function() {
        // Reset links
        linkElements
          .transition()
          .duration(200)
          .attr('opacity', 0.6)
          .attr('stroke-width', d => Math.max(1, d.value));
      });

    // Node labels
    nodeElements.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .style('font-size', '10px')
      .style('font-weight', 'bold')
      .style('fill', d => getContrastText(groupColors[d.group]))
      .style('pointer-events', 'none')
      .text(d => d.name.split(' ').map(w => w[0]).join(''));

    // Connection count badges
    nodeElements.append('circle')
      .attr('cx', d => 8 + (d.connectionCount * 2))
      .attr('cy', d => -(8 + (d.connectionCount * 2)))
      .attr('r', 8)
      .attr('fill', '#2196F3')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    nodeElements.append('text')
      .attr('x', d => 8 + (d.connectionCount * 2))
      .attr('y', d => -(8 + (d.connectionCount * 2)) + 3)
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => d.connectionCount);

    // Update positions on tick
    simulation.on('tick', () => {
      linkElements
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      nodeElements
        .attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Cleanup
    return () => {
      simulation.stop();
    };

  }, [dimensions, filteredRelationships, entities]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 2,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Relationship Strength
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            { label: 'Strong (70+)', color: '#4CAF50' },
            { label: 'Moderate (40-69)', color: '#FF9800' },
            { label: 'Weak (<40)', color: '#F44336' },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                }}
              />
              <Typography variant="caption">{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

export default PatentSuitsGraph;